import React, { useState } from 'react';
import { useTableContext } from '../../context';
import { FilterPanel } from '../toolbar/filter-panel';
import { Filter, FilterOperator } from '../../types';

/**
 * Props for TableFiltersSection component
 */
export interface TableFiltersSectionProps {
  /** Array of filter configurations */
  filterConfigs?: Array<{
    field: string;
    label: string;
    type: 'text' | 'select' | 'date' | 'number';
    options?: Array<{ label: string; value: unknown }>;
  }>;
}

/**
 * TableFiltersSection component - Renders the table filters section
 *
 * This component manages the filter panel state and renders the filter controls
 * when filters are enabled. It integrates with the table context for filter management.
 *
 * @template T - The type of data rows in the table
 */
export function TableFiltersSection<T extends Record<string, unknown>>({
  filterConfigs = [],
}: TableFiltersSectionProps) {
  const { showFilters, filtering } = useTableContext<T>();
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);

  if (!showFilters || filterConfigs.length === 0) {
    return null;
  }

  const handleFiltersChange = (filters: Filter[]) => {
    filtering.clearFilters();
    filters.forEach((filter: Filter) => filtering.addFilter(filter));
  };

  // Convert context filters (operator: string) to strict Filter[] (operator: FilterOperator)
  const currentFilters: Filter[] = filtering.filters.map((f) => ({
    field: f.field,
    operator: f.operator as FilterOperator,
    value: f.value,
  }));

  return (
    <FilterPanel
      filters={currentFilters}
      filterConfigs={filterConfigs}
      onFiltersChange={handleFiltersChange}
      isOpen={filterPanelOpen}
      onToggle={() => setFilterPanelOpen(!filterPanelOpen)}
    />
  );
}

TableFiltersSection.displayName = 'TableFiltersSection';
